import { useState, type FormEvent } from "react";
import { useOutletContext } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { signInWithEmail, type StaffSessionView } from "@/lib/auth";

const fieldClass =
  "border-input bg-background w-full rounded-md border px-3 py-2 text-sm shadow-sm";

type ShellContext = {
  session: StaffSessionView | null;
  refreshSession: () => Promise<void>;
};

/**
 * Staff login (ADR-019). After a valid session the shell returns to the
 * intended dashboard page. Path restaurant context is unchanged (ADR-021).
 */
export function LoginPage() {
  const { refreshSession } = useOutletContext<ShellContext>();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result = await signInWithEmail(email, password);
      if (!result.ok) {
        setError("Could not sign in. Check email and password.");
        return;
      }
      await refreshSession();
    } catch {
      setError("Could not sign in.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
        <p className="text-muted-foreground text-sm">
          Staff email and password. Restaurant, menu, orders, and admin require a
          session.
        </p>
      </div>
      <Card className="max-w-md">
        <CardHeader>
          <CardTitle>Operator login</CardTitle>
          <CardDescription>Better Auth session for the dashboard host only.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="flex flex-col gap-4" onSubmit={(event) => void onSubmit(event)}>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">Email</span>
              <input
                className={fieldClass}
                type="email"
                name="email"
                autoComplete="username"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">Password</span>
              <input
                className={fieldClass}
                type="password"
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </label>
            {error ? <p className="text-destructive text-sm">{error}</p> : null}
            <Button type="submit" disabled={submitting}>
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
